import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AlertTriangle, X, Send, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';

const REPORT_REASONS = [
  { value: 'fake_profile', label: 'Faux profil / Usurpation d\'identité' },
  { value: 'inappropriate_content', label: 'Contenu inapproprié / Pornographique' },
  { value: 'harassment', label: 'Harcèlement / Comportement abusif' },
  { value: 'spam', label: 'Spam / Publicité non sollicitée' },
  { value: 'underage', label: 'Mineur suspecté' },
  { value: 'scam', label: 'Arnaque / Escroquerie' },
  { value: 'other', label: 'Autre raison' }
];

export default function ReportModal({ isOpen, onClose, userId, userPseudo }) {
  const { api } = useAuth();
  const [selectedReason, setSelectedReason] = useState('');
  const [details, setDetails] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedReason) {
      toast.error('Veuillez sélectionner une raison');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/users/${userId}/report`, {
        reason: selectedReason,
        details: details.trim() || null
      });
      
      toast.success('Signalement envoyé. Notre équipe va examiner ce profil.');
      onClose();
      setSelectedReason('');
      setDetails('');
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Erreur lors de l\'envoi du signalement');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setSelectedReason('');
      setDetails('');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        className="bg-dark-paper border-white/10 max-w-md max-h-[90vh] overflow-hidden flex flex-col"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-3 text-white">
            <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <span className="block">Signaler un profil</span>
              <span className="text-sm font-normal text-gray-400">{userPseudo}</span>
            </div>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto space-y-6 mt-4 pr-2">
          {/* Reason Selection */}
          <div className="space-y-3">
            <Label className="text-gray-400">Raison du signalement *</Label>
            <div className="space-y-2">
              {REPORT_REASONS.map((reason) => (
                <label
                  key={reason.value}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedReason === reason.value
                      ? 'border-red-500 bg-red-500/10'
                      : 'border-white/10 hover:border-white/20 bg-dark-subtle'
                  }`}
                >
                  <input
                    type="radio"
                    name="reason"
                    value={reason.value}
                    checked={selectedReason === reason.value}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    selectedReason === reason.value
                      ? 'border-red-500'
                      : 'border-gray-500'
                  }`}>
                    {selectedReason === reason.value && (
                      <div className="w-2 h-2 rounded-full bg-red-500" />
                    )}
                  </div>
                  <span className="text-gray-300 text-sm">{reason.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Additional Details */}
          <div className="space-y-2">
            <Label className="text-gray-400">Détails supplémentaires (optionnel)</Label>
            <Textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Décrivez le problème en quelques mots..."
              className="input-gold min-h-[80px] resize-none"
              maxLength={500}
            />
            <p className="text-xs text-gray-500 text-right">{details.length}/500</p>
          </div>

          {/* Info Notice */}
          <div className="bg-dark-subtle border border-white/10 rounded-lg p-3">
            <p className="text-xs text-gray-400">
              Votre signalement sera traité de manière confidentielle.
            </p>
          </div>

          {/* Actions - Sticky at bottom */}
          <div className="flex gap-3 pt-2 pb-1 sticky bottom-0 bg-dark-paper">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 border-white/20 hover:bg-white/5"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={loading || !selectedReason}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              data-testid="submit-report-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Signaler
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
